import pRetry from 'p-retry';
import { type StateCreator } from 'zustand';
import { getSafeErrorFields } from '../errors';
import { type SessionRecordingStore } from '../types';
import { logger } from './lib/logger';
import { pokeRetryDrain } from './v2/retryDrainV2';

export interface ConnectivitySlice {
  waitForConnectivity: (signal?: AbortSignal) => Promise<void>;
  connectivityPromise: null | Promise<void>;
}

const abortError = (signal: AbortSignal): Error =>
  signal.reason instanceof Error
    ? signal.reason
    : new DOMException('connectivity wait aborted', 'AbortError');

// An aborted caller stops waiting; the shared check itself keeps running for
// the callers that did not abort.
const waitAbortable = (work: Promise<void>, signal: AbortSignal): Promise<void> => {
  if (signal.aborted) return Promise.reject(abortError(signal));
  return new Promise<void>((resolve, reject) => {
    const onAbort = () => reject(abortError(signal));
    signal.addEventListener('abort', onAbort, { once: true });
    work.then(
      () => {
        signal.removeEventListener('abort', onAbort);
        resolve();
      },
      (err: unknown) => {
        signal.removeEventListener('abort', onAbort);
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    );
  });
};

export const createConnectivitySlice: StateCreator<
  SessionRecordingStore,
  [],
  [],
  ConnectivitySlice
> = (set, get) => ({
  connectivityPromise: null,

  waitForConnectivity: async (signal) => {
    if (signal?.aborted) throw abortError(signal);
    const checkConnectivity = get().config?.checkConnectivity;
    if (!checkConnectivity) return;

    // Single-flight regardless of caller signal — concurrent callers must
    // never spawn a second 999-retry health-check loop.
    let promise = get().connectivityPromise;
    if (!promise) {
      let sawOutage = false;
      const created: Promise<void> = pRetry(() => checkConnectivity(), {
        retries: 999,
        maxTimeout: 2 * 60 * 1000, // 2 minutes
        onFailedAttempt: (err) => {
          sawOutage = true;
          logger.error({
            event_type: 'CONNECTIVITY_CHECK_FAILED',
            details: getSafeErrorFields(err),
          });
        },
      })
        .then(() => {
          // A recovered outage is the drain's cue; a first-try success is not.
          if (!sawOutage) return;
          try {
            pokeRetryDrain(set, get, 'connectivity-regained');
          } catch (err) {
            // Every upload awaits this promise: a throw here would turn a
            // passing connectivity check into an upload failure.
            logger.error({
              event_type: 'RETRY_DRAIN_POKE_FAILED',
              details: getSafeErrorFields(err),
            });
          }
        })
        .finally(() => {
          set((state) =>
            state.connectivityPromise === created
              ? { ...state, connectivityPromise: null }
              : state
          );
        });
      promise = created;
      set((state) => ({ ...state, connectivityPromise: created }));
    }

    return signal ? waitAbortable(promise, signal) : promise;
  },
});
